import React, { useCallback, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { parseFloatAndRound } from '@/utils/helpers'

import VenueStatusProgressBar from './VenueStatusProgressBar'
import { VenueStatusColor } from './types'

function VenueStatusBanner({
  rewardDollarAmount = '$10',
  redeemedOrEarnedAmount = '$10',
  availableAmount = null,
  points = 0,
  threshold = 100,
  nextPoints = 0,
  previousProgress = 0,
  title = '',
  animated = false,
  werePointsRedeemed = false,
  werePointsEarned = false,
  firstTimeBonusPoints = undefined,
  vipMultiplier = 0,
  vip = false,
  onAnimationComplete = undefined,
}: {
  rewardDollarAmount?: string | null | undefined
  redeemedOrEarnedAmount?: string | null | undefined
  availableAmount?: string | null | undefined
  points: number | null | undefined
  threshold: number | undefined
  nextPoints: number | null | undefined
  previousProgress?: number | null | undefined
  title?: string | undefined
  animated?: boolean | undefined
  werePointsRedeemed?: boolean | undefined
  werePointsEarned?: boolean | undefined
  firstTimeBonusPoints?: number | undefined
  vipMultiplier?: number | undefined
  vip?: boolean | undefined
  onAnimationComplete?: () => void
}) {
  const router = useRouter()
  const { venue_uuid, tab_uuid, s2p, ordering } = router.query

  const [parsedRedeemedOrEarnedAmount, setParsedRedeemedOrEarnedAmount] = useState(parseFloat(redeemedOrEarnedAmount ?? '0.00'))

  const onAnimationCompletion = useCallback(() => {
    if (onAnimationComplete) {
      onAnimationComplete()
    }
  }, [])

  useEffect(() => {
    setParsedRedeemedOrEarnedAmount(parseFloat(redeemedOrEarnedAmount ?? '0.00'))
  }, [redeemedOrEarnedAmount])

  const isFirstTime = (): boolean => {
    return firstTimeBonusPoints !== null && firstTimeBonusPoints !== undefined && firstTimeBonusPoints > 0
  }

  const crownImage = (): string => {
    return vip ? '/VenueStatus/yellow_crown.svg' : '/VenueStatus/purple_crown.svg'
  }

  const getColor = (): string => {
    return vip ? VenueStatusColor.vip_yellow : VenueStatusColor.electric_purple_1
  }

  const getAppliedOrRedemeedComponent = (): React.JSX.Element | null => {
    if (werePointsRedeemed && parsedRedeemedOrEarnedAmount > 0) {
      if (availableAmount && parseFloat(availableAmount) > 0) {
        return (
          <span className={'font-bold text-[14px] uppercase'}>
            ${parseFloatAndRound(redeemedOrEarnedAmount, 0)} APPLIED, ${parseFloatAndRound(availableAmount, 0)} AVAILABLE
          </span>
        )
      }
      return (
        <span className={'font-bold text-[14px] uppercase'}>
          ${parseFloatAndRound(redeemedOrEarnedAmount, 0)} reward APPLIED
        </span>
      )
    }

    if (werePointsEarned && parsedRedeemedOrEarnedAmount > 0) {
      return (
        <span className={'font-bold text-[14px] uppercase'}>
          ${parseFloatAndRound(redeemedOrEarnedAmount, 0)} reward EARNED
        </span>
      )
    }

    return null
  }

  const werePointsRedeemedOrEarned = (): boolean => {
    return (werePointsRedeemed || werePointsEarned) && parsedRedeemedOrEarnedAmount > 0
  }

  const goToStatus = () => {
    // Navigate to a new URL with venue_uuid query parameter.
    if (router.pathname === '/status-details') return
    router.push(
      `/status-details?venue_uuid=${venue_uuid}${tab_uuid ? `&tab_uuid=${tab_uuid}` : ''}${
        ordering === 'false' ? '&ordering=false' : ''
      }${s2p === 'true' ? '&s2p=true' : ''}&previous=${router.pathname}`
    )
  }

  return (
    <div className="w-full bg-[black]" onClick={goToStatus}>
      <div className="flex w-full ml-[16px] mt-[12px] mb-[8px]">
        {title && (
          <h1 className="text-stone-50 text-[18px] max-w-[243px] max-h-[50px] overflow-hidden font-bold uppercase">
            {title} status
          </h1>
        )}
        {vip && !isFirstTime() ? (
          <>
            <img src="/VenueStatus/vip_circle.svg" className="w-[18px] h-[18px] self-center ml-[8px]" />
            {vipMultiplier && vipMultiplier > 1 && (
              <p className="text-[14px] self-center ml-[2px]">
                <span className={'font-bold text-[#F7E224]'}>x{vipMultiplier ?? 0} pts</span>
              </p>
            )}
          </>
        ) : null}
        {isFirstTime() ? (
          <p className="text-[14px] self-center ml-[8px]">
            <span className={`font-bold`} style={{ color: getColor() }}>
              +{firstTimeBonusPoints ?? 0} bonus {firstTimeBonusPoints === 1 ? 'pt' : 'pts'}
            </span>
          </p>
        ) : null}
      </div>
      <VenueStatusProgressBar
        progress={points}
        previousProgress={previousProgress ?? 0}
        animate={animated}
        maximum={threshold}
        progressBarColor={[getColor()]}
        onAnimationComplete={onAnimationCompletion}
      />
      <div className="w-full flex">
        <p className="text-stone-50 flex-1 text-[14px] ml-[16px] mt-[4px] ">
          {points}/{threshold}
        </p>
        <p className="text-stone-50 flex-1 text-[14px] text-right mr-[16px] mt-[4px]">
          ${parseFloatAndRound(rewardDollarAmount, 0)} off
        </p>
      </div>
      <div className="w-full flex justify-center flex-row mt-[4px]">
        <div className="shrink self-center">
          <img src={crownImage()} className="w-[14px] h-[11px]" />
        </div>
        <div className="shrink self-center ml-[4px] mr-[4px]">
          <p className="text-stone-50 text-[14px]">
            {werePointsRedeemedOrEarned() ? (
              ''
            ) : (
              <span className="font-bold" style={{ color: getColor() }}>
                {nextPoints ?? 0} {nextPoints === 1 ? 'pt' : 'pts'}
              </span>
            )}
            &nbsp;
            {werePointsRedeemedOrEarned() ? getAppliedOrRedemeedComponent() : 'to Reward'}
          </p>
        </div>
        {werePointsRedeemedOrEarned() ? (
          <div className="shrink self-center">
            <img src={crownImage()} className="w-[14px] h-[11px]" />
          </div>
        ) : null}
      </div>
      <div className="w-full flex justify-center flex-row mt-[8px] mb-[12px]">
        {werePointsEarned && (
          <div className="shrink self-center">
            <p className="text-[#9B989B] font-medium text-[12px]">Points are available for use next visit&nbsp;</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default VenueStatusBanner
